/**
 * Whether the caller may operate the platform, discovered by asking.
 *
 * `/control/me` deliberately answers no such question for the ops plane --
 * deriving one there would put a second answer to "may this person operate
 * the platform" in a place built to answer a narrower one. So this hook
 * asks a real endpoint instead:
 * `GET /ops/tenants`, the register's own data, which the ops shell would
 * fetch anyway. A `403` is the answer "no", not a fault.
 *
 * `retry: false`: TanStack Query's default retries any thrown error, and a
 * `403` is not a transient one -- retrying it three times before the shell
 * can render the not-an-operator state teaches an operator's access log to
 * ignore four identical refusals per page load.
 */

import { useQuery } from '@tanstack/react-query';

import { useAuthState } from '../../app/session/authState';
import { useApiClient } from '../../app/session/client';
import type { ApiFailure, ApiFailureError } from '../../lib/http/errors';
import { opsKeys } from '../api/queries';
import { type OpsTenantRow } from '../api/schemas';
import { routePath } from '../../lib/http/routePath';
import { OPS_GET_ROUTES } from '../api/getRoutes';

export type OpsEntitlement =
  | { status: 'checking' }
  | { status: 'entitled' }
  | { status: 'refused' } // the API answered 403
  | { status: 'signed-out' } // 401, or the session already says signed out
  | { status: 'unavailable'; failure: ApiFailure };

/** Asks a real endpoint. Reads nothing off the caller's signed-in identity
 *  beyond whether one exists. */
export function useOpsEntitlement(): OpsEntitlement {
  const client = useApiClient();
  const session = useAuthState();

  const query = useQuery<OpsTenantRow[], ApiFailureError>({
    queryKey: opsKeys.tenants(),
    queryFn: () =>
      client.getParsed(routePath(OPS_GET_ROUTES.opsTenants, {}), OPS_GET_ROUTES.opsTenants.schema),
    retry: false,
    // Only fires once the session is definitely 'signed-in'. `'loading'` --
    // Firebase hasn't answered `onAuthStateChanged` yet -- gets `ApiClient`
    // throwing `unauthenticated` before it ever calls fetch, and with
    // `retry: false` and no remount between ops routes that failure is
    // permanent for the life of the page. `'signed-out'` is excluded too:
    // there is no token to send, and the branch below already answers
    // 'signed-out' from the session alone, with no request to make.
    enabled: session.status === 'signed-in',
  });

  if (session.status === 'signed-out') return { status: 'signed-out' };
  if (query.isSuccess) return { status: 'entitled' };
  if (query.isError) {
    const { failure } = query.error;
    if (failure.kind === 'forbidden') return { status: 'refused' };
    if (failure.kind === 'unauthenticated') return { status: 'signed-out' };
    return { status: 'unavailable', failure };
  }
  return { status: 'checking' };
}
